import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
  Paper,
  Chip,
  makeStyles,
  FormHelperText
} from '@material-ui/core';
import { Delete as DeleteIcon, Add as AddIcon } from '@material-ui/icons';
import { useTranslation } from '../hooks/useTranslation';

const useStyles = makeStyles((theme) => ({
  formSection: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  fieldContainer: {
    marginBottom: theme.spacing(2),
  },
  possibleValuesSection: {
    padding: theme.spacing(2),
    backgroundColor: 'var(--ikaro-gray-light)',
    borderRadius: 4,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  valueChip: {
    margin: theme.spacing(0.5),
  },
  valuesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
    minHeight: 40,
  },
  addValueContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  addValueInput: {
    flexGrow: 1,
    marginRight: theme.spacing(1),
  },
  emptyValuesText: {
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
  dialogContent: {
    paddingTop: theme.spacing(1),
    maxHeight: '80vh',
    overflow: 'auto',
  },
}));

const NodeEditDialog = ({ open, onClose, onSave, nodeData }) => {
  const classes = useStyles();
  const { t } = useTranslation('editor');
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    description: '',
    type: 'string',
    list: false,
    possible_values: [],
    isLeaf: false,
    default: ''
  });
  const [newValueText, setNewValueText] = useState('');
  const [errors, setErrors] = useState({});
  
  // Wrapper de segurança para eventos - função utilitária para lidar com todos os eventos
  const safeEventHandler = useCallback((handler) => {
    return (e) => {
      try {
        // Se o evento for nulo ou indefinido, não fazer nada
        if (!e) {
          console.warn('Evento nulo ou indefinido recebido', {
            handler: handler.toString().substring(0, 100) + '...',
            callStack: new Error().stack
          });
          return;
        }
        
        // Para eventos do React, chamar persist() para evitar problemas com o pool de eventos
        if (e && typeof e.persist === 'function') {
          e.persist();
        }

        // Se o evento for um valor primitivo (não um objeto), 
        // isso provavelmente é uma chamada direta com um valor, não um evento
        if (typeof e !== 'object') {
          handler(e);
          return;
        }

        // Para eventos normais que têm target
        if (e && e.target) {
          // Extrair o valor antes que o evento seja liberado pelo pool
          const target = e.target;
          const value = target.value;
          const type = target.type;
          const checked = target.checked;
          const name = target.name;
          
          // Criar um novo objeto de evento com os valores extraídos
          const syntheticEvent = {
            ...e,
            target: {
              value,
              type,
              checked,
              name
            },
            currentTarget: target,
            // Preservar métodos importantes
            preventDefault: e.preventDefault ? () => e.preventDefault() : () => {},
            stopPropagation: e.stopPropagation ? () => e.stopPropagation() : () => {}
          };
          
          handler(syntheticEvent);
          return;
        }

        // Outros tipos de eventos que não têm target
        if (e && !e.target) {
          handler(e);
          return;
        }

        console.warn('Evento não reconhecido', e, {
          handler: handler.toString().substring(0, 100) + '...',
          callStack: new Error().stack
        });
      } catch (error) {
        console.error('Erro ao processar evento:', error, {
          event: e,
          handler: handler.toString().substring(0, 100) + '...',
          callStack: error.stack
        });
      }
    };
  }, []);

  // Quando o componente abre ou muda o nodeData, atualizar o estado
  useEffect(() => {
    if (nodeData) {
      setFormData({
        name: nodeData.name || '',
        label: nodeData.label || '',
        description: nodeData.description || '',
        type: nodeData.type || 'string',
        list: nodeData.list || false,
        possible_values: nodeData.possible_values || [],
        isLeaf: nodeData.isLeaf || false,
        default: nodeData.default || ''
      });
    }
  }, [nodeData]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('nodeEditDialog.validation.nameRequired');
    }
    
    if (formData.isLeaf && !formData.label.trim()) {
      newErrors.label = t('nodeEditDialog.validation.labelRequired');
    }
    
    if (formData.type === "possible_values" && formData.possible_values.length === 0) {
      newErrors.possibleValues = t('nodeEditDialog.validation.possibleValuesRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleTypeChange = (newType) => {
    // Garantir que temos um valor válido
    try {
      if (newType === null || newType === undefined) return;
      
      // Atualizar o tipo
      setFormData(prev => {
        const updated = { ...prev, type: newType };
        
        // Se o tipo for "possible_values", resetar a lista de valores possíveis
        if (newType === "possible_values") {
          updated.possible_values = [];
        }
        
        return updated;
      });
    } catch (error) {
      console.error("Error changing type:", error);
    }
  };

  const addPossibleValue = () => {
    if (!newValueText.trim()) return;
    
    if (formData.possible_values.includes(newValueText.trim())) {
      alert(t('nodeEditDialog.validation.valueAlreadyExists'));
      return;
    }
    
    setFormData(prev => ({ ...prev, possible_values: [...prev.possible_values, newValueText.trim()] }));
    setNewValueText("");
  };

  const removePossibleValue = (valueToRemove) => {
    setFormData(prev => ({ ...prev, possible_values: prev.possible_values.filter(value => value !== valueToRemove) }));
  };

  // Tratadores específicos que dependem de outras funções
  const handleNameChange = useCallback((e) => {
    // Obtém o valor de forma segura, mesmo se o evento tiver sido modificado pelo safeEventHandler
    const value = e && e.target ? e.target.value : (typeof e === 'string' ? e : '');
    if (value !== undefined) {
      setFormData(prev => ({ ...prev, name: value }));
    }
  }, []);

  const handleLabelChange = useCallback((e) => {
    // Obtém o valor de forma segura, mesmo se o evento tiver sido modificado pelo safeEventHandler
    const value = e && e.target ? e.target.value : (typeof e === 'string' ? e : '');
    if (value !== undefined) {
      setFormData(prev => ({ ...prev, label: value }));
    }
  }, []);

  const handleDescriptionChange = useCallback((e) => {
    // Obtém o valor de forma segura, mesmo se o evento tiver sido modificado pelo safeEventHandler
    const value = e && e.target ? e.target.value : (typeof e === 'string' ? e : '');
    if (value !== undefined) {
      setFormData(prev => ({ ...prev, description: value }));
    }
  }, []);

  const handleNewValueChange = useCallback((e) => {
    // Obtém o valor de forma segura, mesmo se o evento tiver sido modificado pelo safeEventHandler
    const value = e && e.target ? e.target.value : (typeof e === 'string' ? e : '');
    if (value !== undefined) {
      setNewValueText(value);
    }
  }, []);

  const handleNewValueKeyPress = useCallback((event) => {
    if (event && event.key === 'Enter' && newValueText.trim()) {
      event.preventDefault();
      addPossibleValue();
    }
  // Estamos omitindo addPossibleValue da lista de dependências para evitar dependência circular
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newValueText]);

  const renderLeafFields = () => (
    <>
      <div className={classes.formSection}>
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          {t('nodeEditDialog.fieldTypeSection')}
        </Typography>
        <FormControl fullWidth variant="outlined" size="small" className={classes.fieldContainer}>
          <InputLabel id="type-select-label">{t('nodeEditDialog.typeLabel')}</InputLabel>
          <Select
            labelId="type-select-label"
            value={formData.type || 'string'}
            onChange={safeEventHandler((e) => {
              const value = e && e.target ? e.target.value : e;
              if (value !== undefined && value !== null) {
                handleTypeChange(value);
              }
            })}
            label={t('nodeEditDialog.typeLabel')}
          >
            <MenuItem value="string">{t('nodeEditDialog.typeText')}</MenuItem>
            <MenuItem value="int">{t('nodeEditDialog.typeInteger')}</MenuItem>
            <MenuItem value="float">{t('nodeEditDialog.typeFloat')}</MenuItem>
            <MenuItem value="date">{t('nodeEditDialog.typeDate')}</MenuItem>
            <MenuItem value="time">{t('nodeEditDialog.typeTime')}</MenuItem>
            <MenuItem value="date_time">{t('nodeEditDialog.typeDateTime')}</MenuItem>
            <MenuItem value="bool">{t('nodeEditDialog.typeBool')}</MenuItem>
            <MenuItem value="possible_values">{t('nodeEditDialog.typePossibleValues')}</MenuItem>
          </Select>
          <FormHelperText>{t('nodeEditDialog.typeHelperText')}</FormHelperText>
        </FormControl>
      </div>

      <div className={classes.formSection}>
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          {t('nodeEditDialog.fieldIdentificationSection')}
        </Typography>
        <FormControl fullWidth className={classes.fieldContainer}>
          <TextField
            label={t('nodeEditDialog.labelLabel')}
            variant="outlined"
            size="small"
            value={formData.label}
            onChange={safeEventHandler(handleLabelChange)}
            error={!!errors.label}
            helperText={errors.label || t('nodeEditDialog.labelHelperText')}
            required
          />
        </FormControl>
      </div>

      <div className={classes.formSection}>
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          {t('nodeEditDialog.fieldDescriptionSection')}
        </Typography>
        <FormControl fullWidth className={classes.fieldContainer}>
          <TextField
            label={t('nodeEditDialog.descriptionLabel')}
            variant="outlined"
            size="small"
            value={formData.description}
            onChange={safeEventHandler(handleDescriptionChange)}
            multiline
            minRows={2}
            helperText={t('nodeEditDialog.descriptionHelperText')}
          />
        </FormControl>
      </div>

      {formData.type === "possible_values" && (
        <div className={classes.formSection}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            {t('nodeEditDialog.possibleValuesSection')}
          </Typography>
          <Paper className={classes.possibleValuesSection}>
            <FormHelperText>
              {t('nodeEditDialog.possibleValuesHelper')}
            </FormHelperText>
            
            <div className={classes.addValueContainer}>
              <TextField
                label={t('nodeEditDialog.newValueLabel')}
                variant="outlined"
                size="small"
                value={newValueText}
                onChange={safeEventHandler(handleNewValueChange)}
                onKeyPress={safeEventHandler(handleNewValueKeyPress)}
                className={classes.addValueInput}
                error={!!errors.possibleValues}
                helperText={errors.possibleValues}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={safeEventHandler(addPossibleValue)}
                disabled={!newValueText.trim()}
              >
                {t('nodeEditDialog.addButton')}
              </Button>
            </div>
            
            <Divider />

            <div className={classes.valuesContainer}>
              {formData.possible_values.length === 0 ? (
                <Typography className={classes.emptyValuesText}>
                  {t('nodeEditDialog.noPossibleValues')}
                </Typography>
              ) : (
                formData.possible_values.map((value, index) => (
                  <Chip
                    key={`${value}-${index}`}
                    label={value}
                    onDelete={safeEventHandler(() => removePossibleValue(value))}
                    className={classes.valueChip}
                    color="primary"
                    variant="outlined"
                    deleteIcon={<DeleteIcon />}
                  />
                ))
              )}
            </div>
          </Paper>
        </div>
      )}
    </>
  );

  return (
    <Dialog 
      open={open} 
      onClose={safeEventHandler(onClose)} 
      fullWidth 
      maxWidth="md"
      aria-labelledby="edit-dialog-title"
    >
      <DialogTitle id="edit-dialog-title">
        {formData.isLeaf ? t('nodeEditDialog.editFieldTitle') : t('nodeEditDialog.editGroupTitle')}
      </DialogTitle>
      
      <DialogContent dividers className={classes.dialogContent}>
        <div className={classes.formSection}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            {t('nodeEditDialog.basicInfoSection')}
          </Typography>
          <FormControl fullWidth className={classes.fieldContainer}>
            <TextField
              label={t('nodeEditDialog.nodeNameLabel')}
              variant="outlined"
              size="small"
              value={formData.name}
              onChange={safeEventHandler(handleNameChange)}
              error={!!errors.name}
              helperText={errors.name || t('nodeEditDialog.nodeNameHelper')}
              required
            />
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={!formData.isLeaf}
                onChange={safeEventHandler(() => {
                  setFormData(prev => ({ ...prev, isLeaf: !prev.isLeaf }));
                })}
                color="primary"
              />
            }
            label={t('nodeEditDialog.isGroupCheckbox')}
          />
        </div>
        
        <Divider />
        
        {formData.isLeaf ? renderLeafFields() : null}
        
        <div className={classes.formSection}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            {t('nodeEditDialog.additionalOptionsSection')}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.list}
                onChange={safeEventHandler(() => {
                  setFormData(prev => ({ ...prev, list: !prev.list }));
                })}
                color="primary"
              />
            }
            label={t('nodeEditDialog.isListCheckbox')}
          />
        </div>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={safeEventHandler(onClose)} color="default" variant="outlined">
          {t('nodeEditDialog.cancelButton')}
        </Button>
        <Button onClick={safeEventHandler(handleSave)} color="primary" variant="contained">
          {t('nodeEditDialog.saveButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NodeEditDialog; 